/**
 * What a phone keeps after a PIN (decisions 39 and 40), per tournament: the rehearsal and the real
 * day never share a session, and neither does the pretend day (?mock=1) on the same site. A
 * player's session says which player; an admin's carries the admin link it was for, kept as soon
 * as an admin link opens on this phone (before its PIN too), so the hidden way in from the
 * players' side knows which admin to ask for.
 *
 * Without storage (private mode) nothing is kept and the phone asks again next time.
 */

export type KeptAs = "player" | "admin";

export interface KeptPlayer {
  session: string;
  player: string;
}

export interface KeptAdmin {
  /** The admin link's token this phone opened. */
  token: string;
  /** Null until its admin PIN is typed on this phone. */
  session: string | null;
  /** A named admin's name; null for the owner. */
  name: string | null;
  owner: boolean;
}

function key(tournament: string, mock: boolean, as: KeptAs): string {
  return `bd.session.${mock ? "mock." : ""}${tournament}.${as}`;
}

function read<T>(k: string): T | null {
  try {
    const raw = localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(k: string, value: unknown): void {
  try {
    if (value === null) localStorage.removeItem(k);
    else localStorage.setItem(k, JSON.stringify(value));
  } catch {
    // without storage the phone asks again next time
  }
}

/** One phone's sessions for one tournament. */
export class Keeper {
  constructor(
    readonly tournament: string,
    readonly mock: boolean,
  ) {}

  player(): KeptPlayer | null {
    const kept = read<KeptPlayer>(key(this.tournament, this.mock, "player"));
    return kept && typeof kept.session === "string" && typeof kept.player === "string" ? kept : null;
  }

  savePlayer(kept: KeptPlayer | null): void {
    write(key(this.tournament, this.mock, "player"), kept);
  }

  admin(): KeptAdmin | null {
    const kept = read<KeptAdmin>(key(this.tournament, this.mock, "admin"));
    return kept && typeof kept.token === "string" && kept.token ? kept : null;
  }

  saveAdmin(kept: KeptAdmin | null): void {
    write(key(this.tournament, this.mock, "admin"), kept);
  }

  /** The session to send in hello for a screen of this role. */
  sessionFor(role: string, token: string): string | undefined {
    if (role === "player") return this.player()?.session;
    if (role === "admin") {
      const kept = this.admin();
      // a session made for another admin link would only be refused: keep it for that link
      return kept && kept.token === token && kept.session ? kept.session : undefined;
    }
    return undefined;
  }
}
